import { useState, useCallback, useEffect } from 'react';
import { useAppDispatch } from '../../../../app/store';
import { Interview, UpdateInterviewRequest } from '../../../../api/Interviews';
import {
    editInterview,
    cancelEditInterview,
    addTemporaryInterview,
    removeTemporaryInterview,
    setCreateInterviewStatus,
    CreateInterviewStatus
} from '../../logic/InterviewAnalysisSlice';
import { createInterview, updateInterview } from '../../../../thunks/Interviews';
import { v4 as uuid } from 'uuid';
import { formatDateForInput, getCurrentDate } from '../../../../lib/utils';
import { validateInterviewFieldChanges } from './helper';
import { InterviewWithState } from '../../logic/InterviewAnalysisSlice';

interface ModalState {
    isOpen: boolean;
    pendingInterviewId: string | null;
}

interface UseInterviewFormManagerParams {
    caseId: string;
    interviews: InterviewWithState[];
    createInterviewStatus: CreateInterviewStatus;
}

interface UseInterviewFormManagerReturn {
    selectedInterviewId: string | null;
    formPayload: Interview | null;
    hasUnsavedChanges: boolean;
    isBusy: boolean;
    modalState: ModalState;
    error: string | null;

    // Actions
    requestSelection: (interviewId: string) => void;
    initiateNewEntry: () => void;
    updateField: <K extends keyof Interview>(key: K, value: Interview[K]) => void;
    saveChanges: () => void;
    discardChanges: () => void;
    resolveConflict: (action: 'DISCARD' | 'CONTINUE') => void;
    clearError: () => void;
}

export const useInterviewFormManager = ({
    caseId,
    interviews,
    createInterviewStatus
}: UseInterviewFormManagerParams): UseInterviewFormManagerReturn => {
    const dispatch = useAppDispatch();
    
    const [selectedInterviewId, setSelectedInterviewId] = useState<string | null>(null);
    const [formPayload, setFormPayload] = useState<Interview | null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [modalState, setModalState] = useState<ModalState>({
        isOpen: false,
        pendingInterviewId: null
    });
    const [error, setError] = useState<string | null>(null);

    // Calculate based on saving status for both creating & updating
    const isBusy = createInterviewStatus === 'saving' || 
                   (selectedInterviewId ? interviews.find(i => i.id === selectedInterviewId)?.status === 'saving' : false);

    // auto select first interview on mount
    useEffect(() => {
        if (interviews.length > 0 && !selectedInterviewId) {
            const firstInterview = interviews[0];
            setSelectedInterviewId(firstInterview.id);
            dispatch(editInterview(firstInterview.id));
            setFormPayload(firstInterview.interview);
            setHasUnsavedChanges(false);
        }
    }, [interviews.length, selectedInterviewId]);

    // Check for unsaved changes
    const checkUnsavedChanges = useCallback((): boolean => {
        if (!selectedInterviewId || !formPayload) return false;
        
        const currentInterview = interviews.find(i => i.id === selectedInterviewId);
        if (!currentInterview) return false;

        const changedFields = validateInterviewFieldChanges(
            currentInterview.interview,
            formPayload
        );
        
        return Object.keys(changedFields).length > 0;
    }, [selectedInterviewId, formPayload, interviews]);

    // Request selection with unsaved changes check
    const requestSelection = useCallback((interviewId: string) => {
        if (isBusy) return; // Don't allow selection while saving
        
        if (selectedInterviewId !== null && selectedInterviewId !== interviewId) {
            if (checkUnsavedChanges()) {
                setModalState({
                    isOpen: true,
                    pendingInterviewId: interviewId
                });
                return;
            }
            
            // No unsaved changes, proceed with selection
            if (selectedInterviewId) {
                dispatch(cancelEditInterview(selectedInterviewId));
            }
        }

        setSelectedInterviewId(interviewId);
        dispatch(editInterview(interviewId));

        const interviewToEdit = interviews.find(i => i.id === interviewId);
        if (interviewToEdit) {
            setFormPayload(interviewToEdit.interview);
            setHasUnsavedChanges(false);
        }
    }, [selectedInterviewId, isBusy, checkUnsavedChanges, interviews, dispatch]);

    // Initiate new entry
    const initiateNewEntry = useCallback(() => {
        // Check if there's already a temporary interview
        const hasTemporaryInterview = interviews.some(i => i.id.startsWith('temp-'));
        if (hasTemporaryInterview) {
            return; // Prohibit adding another temporary interview if one exists
        }

        // Check for unsaved changes
        if (selectedInterviewId !== null && checkUnsavedChanges()) {
            setModalState({
                isOpen: true,
                pendingInterviewId: null
            });
            return;
        }

        // Create temporary interview
        const tempId = `temp-${uuid()}`;
        const newInterview: Interview = {
            id: tempId,
            case_id: caseId,
            interviewee_type: '',
            date: formatDateForInput(getCurrentDate()),
            mode: null as any,
            investigator_email: '',
            findings: '',
            evidence_links: []
        };

        dispatch(addTemporaryInterview({
            id: tempId,
            interview: newInterview,
            status: 'editing'
        }));

        setSelectedInterviewId(tempId);
        setFormPayload(newInterview);
        setHasUnsavedChanges(false);
        setError(null);
    }, [interviews, selectedInterviewId, checkUnsavedChanges, caseId, dispatch]);

    // field update
    const updateField = useCallback(<K extends keyof Interview>(
        key: K,
        value: Interview[K]
    ) => {
        setFormPayload(prev => ({
            ...(prev as Interview),
            [key]: value
        }));
        setHasUnsavedChanges(true);
    }, []);

    // Save changes (create or update)
    const saveChanges = useCallback(() => {
        if (!selectedInterviewId || !formPayload) return;

        const existingInterview = interviews.find(i => i.id === selectedInterviewId);
        if (!existingInterview) return;

        setError(null);

        // If temporary interview, create it
        if (selectedInterviewId.startsWith('temp-')) {
            const newInterview = { ...formPayload };
            const realId = uuid();
            newInterview.id = realId;
            newInterview.case_id = caseId;

            dispatch(createInterview({ interviews: [newInterview] }))
                .unwrap()
                .then(() => {
                    dispatch(removeTemporaryInterview(selectedInterviewId));
                    setSelectedInterviewId(realId);
                    setFormPayload(newInterview);
                    setHasUnsavedChanges(false);
                })
                .catch((err) => {
                    console.error('Failed to create interview:', err);
                    setError('Failed to save interview. Please try again.');
                });
            
            return;
        }

        // Otherwise, update existing interview
        const request: UpdateInterviewRequest = {};
        const original = existingInterview.interview;
        
        if (formPayload.interviewee_type !== original.interviewee_type)
            request.interviewee_type = formPayload.interviewee_type;
        if (formPayload.interviewee_name !== original.interviewee_name)
            request.interviewee_name = formPayload.interviewee_name;
        if (formPayload.date !== original.date)
            request.date = formPayload.date;
        if (formPayload.mode !== original.mode)
            request.mode = formPayload.mode;
        if (formPayload.investigator_email !== original.investigator_email)
            request.investigator_email = formPayload.investigator_email;
        if (formPayload.findings !== original.findings)
            request.findings = formPayload.findings;
        if (formPayload.evidence_links !== original.evidence_links)
            request.evidence_links = formPayload.evidence_links;

        dispatch(updateInterview({
            caseId,
            id: selectedInterviewId,
            request
        }))
            .unwrap()
            .then(() => {
                setHasUnsavedChanges(false);
            })
            .catch((err) => {
                console.error('Failed to update interview:', err);
                setError('Failed to update interview. Please try again.');
            });
    }, [selectedInterviewId, formPayload, interviews, caseId, dispatch]);

    // Discard changes
    const discardChanges = useCallback(() => {
        if (!selectedInterviewId) return;

        // If temporary interview, remove it
        if (selectedInterviewId.startsWith('temp-')) {
            dispatch(removeTemporaryInterview(selectedInterviewId));
        } else {
            dispatch(cancelEditInterview(selectedInterviewId));
        }

        setHasUnsavedChanges(false);
        setError(null);

        // Reset to first non-temporary interview or null
        const remainingInterviews = interviews.filter(i => i.id !== selectedInterviewId);
        if (remainingInterviews.length > 0) {
            const firstInterview = remainingInterviews[0];
            setSelectedInterviewId(firstInterview.id);
            dispatch(editInterview(firstInterview.id));
            setFormPayload(firstInterview.interview);
        } else {
            setFormPayload(null);
            setSelectedInterviewId(null);
        }
    }, [selectedInterviewId, interviews, dispatch]);

    // Resolve modal conflict
    const resolveConflict = useCallback((action: 'DISCARD' | 'CONTINUE') => {
        const { pendingInterviewId } = modalState;

        if (action === 'DISCARD') {
            // Discard current changes
            if (selectedInterviewId?.startsWith('temp-')) {
                dispatch(removeTemporaryInterview(selectedInterviewId));
            } else if (selectedInterviewId) {
                dispatch(cancelEditInterview(selectedInterviewId));
            }

            setHasUnsavedChanges(false);
            setError(null);

            // Handle pending action
            if (pendingInterviewId !== null) {
                // Navigate to pending interview
                setSelectedInterviewId(pendingInterviewId);
                dispatch(editInterview(pendingInterviewId));
                
                const interviewToEdit = interviews.find(i => i.id === pendingInterviewId);
                if (interviewToEdit) {
                    setFormPayload(interviewToEdit.interview);
                }
            } else {
                // User wanted to add new interview, create it directly
                const tempId = `temp-${uuid()}`;
                const newInterview: Interview = {
                    id: tempId,
                    case_id: caseId,
                    interviewee_type: '',
                    date: formatDateForInput(getCurrentDate()),
                    mode: null as any,
                    investigator_email: '',
                    findings: '',
                    evidence_links: []
                };

                dispatch(addTemporaryInterview({
                    id: tempId,
                    interview: newInterview,
                    status: 'editing'
                }));

                setSelectedInterviewId(tempId);
                setFormPayload(newInterview);
            }
        } else {
            document.getElementById('interview-findings')?.scrollIntoView({ behavior: 'smooth' });
        }

        setModalState({ isOpen: false, pendingInterviewId: null });
    }, [modalState, selectedInterviewId, interviews, dispatch, initiateNewEntry]);

    // Clear error
    const clearError = useCallback(() => {
        setError(null);
        if (createInterviewStatus === 'error') {
            dispatch(setCreateInterviewStatus('idle'));
        }
    }, [createInterviewStatus, dispatch]);

    return {
        selectedInterviewId,
        formPayload,
        hasUnsavedChanges,
        isBusy,
        modalState,
        error,
        requestSelection,
        initiateNewEntry,
        updateField,
        saveChanges,
        discardChanges,
        resolveConflict,
        clearError
    };
};
